/* eslint-disable ts/ban-ts-comment */
import { APP_ROUTER, cityName, provinceName } from '@qctsw/common'
import { isString, timeFormat } from '@qctsw/utils'
import type { FormInst } from 'naive-ui'
import type { CarSelected } from '~/components/Basic/CarSelectFilter/hooks'
import type { OmitPageParam } from '~/composables/usePagination'
import { logicIssues } from '~/logic/common/issue'
import { logicParts } from '~/logic/common/part'
import { carLevel } from '~/pages/reputation/hooks/filter'
import type { ComplaintSearchParams } from '~/server/types'
import { CAR_INSURANCE_CODE, ISSUE_TYPE_CODE_ENUM } from '~/shared/constants'

export const defaultForm = {
  timePicker: undefined, // [start, end] timestamp
  carBrandId: undefined,
  carBrandInfo: undefined,
  carSeriesId: undefined,
  carSeriesInfo: undefined,
  seriesGrade: undefined,
  quality: [] as number[],
  sale: [] as number[],
  afterSale: [] as number[],
  appeal: [] as number[],
  areaIds: [undefined, undefined] as [string | undefined, string | undefined],
  insuranceCode: undefined,
  part_1: [] as number[],
  part_2: [] as number[],
  part_3: [] as number[],
  part_4: [] as number[],
  seriesEnergyTypeEnum: undefined,
}

export function useComplaintFilter() {
  const route = useRoute()

  const { state: issuesState, issues, getHandle: getIssuesHandle } = logicIssues()
  const { state: partsState, parts, getHandle: getPartsHandle } = logicParts([1, 2, 3, 4])

  const formRef = ref<FormInst | null>(null)
  const formData = ref<{
    title?: string
    id?: string
    timePicker?: [number, number]
    carBrandId?: number
    carBrandInfo?: { id: number, name: string }
    carSeriesId?: number
    carSeriesInfo?: { id: number, name: string }
    seriesGrade?: typeof carLevel[number]['value']
    quality: number[]
    sale: number[]
    afterSale: number[]
    appeal: number[]
    areaIds: [string | undefined, string | undefined]
    insuranceCode?: typeof CAR_INSURANCE_CODE[number]['value']
    part_1: number[]
    part_2: number[]
    part_3: number[]
    part_4: number[]
    seriesEnergyTypeEnum: string | undefined
    vendorId?: number
    carVendorInfo?: { id: number, name: string }
  }>({
    title: undefined,
    id: undefined,
    timePicker: undefined, // [start, end] timestamp
    carBrandId: undefined,
    carBrandInfo: undefined,
    carSeriesId: undefined,
    carSeriesInfo: undefined,
    seriesGrade: undefined,
    quality: [],
    sale: [],
    afterSale: [],
    appeal: [],
    areaIds: [undefined, undefined],
    insuranceCode: undefined,
    part_1: [],
    part_2: [],
    part_3: [],
    part_4: [],
    seriesEnergyTypeEnum: undefined,
    carVendorInfo: undefined,
    vendorId: undefined,
  })

  function carSelected(data: CarSelected) {
    formData.value.carBrandId = data.brand?.id
    formData.value.carBrandInfo = data.brand ? { id: data.brand.id, name: data.brand.brandName } : undefined
    formData.value.carSeriesId = data.series?.id
    formData.value.carSeriesInfo = data.series ? { id: data.series.id, name: data.series.carSeriesName } : undefined
  }

  const pickerChangeShow = ref(true)
  function _changePickerShowHandler() {
    pickerChangeShow.value = false
    requestAnimationFrame(() => {
      pickerChangeShow.value = true
    })
  }

  function getShowValue(key: keyof typeof formData.value): undefined | string | { label: string, value: number | string }[] {
    if (formData.value[key] === undefined)
      return
    if (key === 'areaIds' && !formData.value.areaIds[0])
      return

    switch (key) {
      case 'vendorId':
        return formData.value.carVendorInfo?.name

      case 'title':
        return formData.value.title
      case 'id':
        return `QT${formData.value.id}`
      case 'timePicker':
        return `${timeFormat(formData.value.timePicker?.[0], 'YYYY/MM/DD')} - ${timeFormat(formData.value.timePicker?.[1], 'YYYY/MM/DD')}`
      case 'carBrandId':
        return formData.value.carBrandInfo?.name
      case 'carSeriesId':
        return formData.value.carSeriesInfo?.name
      case 'seriesGrade':
        return formData.value.seriesGrade === 'UNLIMITED' ? '' : carLevel.find(v => v.value === formData.value.seriesGrade)?.label
      case 'areaIds':
        return Array.from(new Set([
          formData.value.areaIds[0] ? provinceName(formData.value.areaIds[0]) : '',
          formData.value.areaIds[1] ? cityName(formData.value.areaIds[1]) : '',
        ])).filter(Boolean).join(' - ')
      case 'insuranceCode':
        return CAR_INSURANCE_CODE.find(v => v.value === formData.value.insuranceCode)?.label
      case 'quality':
        return issues.value[ISSUE_TYPE_CODE_ENUM.quality]
          .filter(v => formData.value.quality.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(质量问题)` }))
      case 'sale':
        return issues.value[ISSUE_TYPE_CODE_ENUM.sale]
          .filter(v => formData.value.sale.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(销售问题)` }))
      case 'afterSale':
        return issues.value[ISSUE_TYPE_CODE_ENUM.afterSale]
          .filter(v => formData.value.afterSale.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(售后问题)` }))
      case 'appeal':
        return issues.value[ISSUE_TYPE_CODE_ENUM.appeal]
          .filter(v => formData.value.appeal.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(诉求)` }))
      case 'part_1':
        return parts.value[1]
          .filter(v => formData.value.part_1.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(轮胎)` }))
      case 'part_2':
        return parts.value[2]
          .filter(v => formData.value.part_2.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(发动机)` }))
      case 'part_3':
        return parts.value[3]
          .filter(v => formData.value.part_3.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(变速箱)` }))
      case 'part_4':
        return parts.value[4]
          .filter(v => formData.value.part_4.includes(v.value))
          .map(v => ({ value: v.value, label: `${v.label}(传动油)` }))
    }
  }

  function clearFilter(key?: keyof typeof formData.value, _value?: any) {
    nextTick(() => {
      if (!key) {
        formData.value = { ...defaultForm }
        _changePickerShowHandler()
        return
      }
      if (key === 'areaIds') {
        formData.value.areaIds = [undefined, undefined]
        _changePickerShowHandler()
      }
      else if (key.match(/^part_/) || ['quality', 'sale', 'afterSale', 'appeal'].includes(key)) {
        // @ts-expect-error
        formData.value[key] = []
      }
      else {
      // @ts-expect-error
        formData.value[key] = undefined
      }

      if (key === 'timePicker')
        _changePickerShowHandler()

      if (key === 'carSeriesId')
        formData.value.carSeriesInfo = undefined
      if (key === 'carBrandId') {
        formData.value.carBrandInfo = undefined
        formData.value.carSeriesId = undefined
        formData.value.carSeriesInfo = undefined
      }
    })
  }

  function initRouteQuery() {
    const query = route.query
    if (!query || route.path !== APP_ROUTER.complaintSearch())
      return
    const {
      title,
      id,
      startTime,
      endTime,
      carBrandId,
      carBrandName,
      carSeriesId,
      carSeriesName,
      seriesGrade,
      provinceId,
      cityId,
      quality,
      sale,
      afterSale,
      appeal,
      insuranceCode,
      part_1,
      part_2,
      part_3,
      part_4,
      seriesEnergyTypeEnum,
      carVendorId,
      carVendorName,
    } = query

    if (seriesEnergyTypeEnum)
      formData.value.seriesEnergyTypeEnum = seriesEnergyTypeEnum as string

    if (carVendorId && !Number.isNaN(Number(carVendorId)) && carVendorName) {
      const id = Number(carVendorId)
      formData.value.vendorId = id
      formData.value.carVendorInfo = { id, name: carVendorName as string }
    }
    if (title)
      formData.value.title = title as string
    if (id)
      formData.value.id = id as string
    if (startTime && endTime && !Number.isNaN(Number(startTime)) && !Number.isNaN(Number(endTime)))
      formData.value.timePicker = [Number(startTime), Number(endTime)] as [number, number]

    if (carBrandId && !Number.isNaN(Number(carBrandId)) && carBrandName) {
      const id = Number(carBrandId)
      formData.value.carBrandId = id
      formData.value.carBrandInfo = { id, name: carBrandName as string }
    }

    if (formData.value.carBrandId && carSeriesName) {
      const id = Number(carSeriesId)
      formData.value.carSeriesId = id
      formData.value.carSeriesInfo = { id, name: carSeriesName as string }
    }
    if (seriesGrade)
      formData.value.seriesGrade = seriesGrade as typeof carLevel[number]['value']

    if (provinceId)
      formData.value.areaIds = [provinceId as string, cityId as string]

    // 投诉问题和诉求
    if (quality)
      formData.value.quality = isString(quality) ? [Number(quality)] : (quality as string[]).map(Number)
    if (sale)
      formData.value.sale = isString(sale) ? [Number(sale)] : (sale as string[]).map(Number)
    if (afterSale)
      formData.value.afterSale = isString(afterSale) ? [Number(afterSale)] : (afterSale as string[]).map(Number)
    if (appeal)
      formData.value.appeal = isString(appeal) ? [Number(appeal)] : (appeal as string[]).map(Number)
    if (insuranceCode)
      formData.value.insuranceCode = Number(insuranceCode) as unknown as typeof CAR_INSURANCE_CODE[number]['value']
    if (part_1)
      formData.value.part_1 = isString(part_1) ? [Number(part_1)] : (part_1 as string[]).map(Number)
    if (part_2)
      formData.value.part_2 = isString(part_2) ? [Number(part_2)] : (part_2 as string[]).map(Number)
    if (part_3)
      formData.value.part_3 = isString(part_3) ? [Number(part_3)] : (part_3 as string[]).map(Number)
    if (part_4)
      formData.value.part_4 = isString(part_4) ? [Number(part_4)] : (part_4 as string[]).map(Number)
  }

  const filterRouteQuery = computed(() => ({
    title: formData.value.title,
    id: formData.value.id,
    startTime: formData.value.timePicker?.[0],
    endTime: formData.value.timePicker?.[1],
    carBrandId: formData.value.carBrandId,
    carBrandName: formData.value.carBrandInfo?.name,
    carSeriesId: formData.value.carSeriesId,
    carSeriesName: formData.value.carSeriesInfo?.name,
    seriesGrade: formData.value.seriesGrade,
    provinceId: formData.value.areaIds[0],
    cityId: formData.value.areaIds[1],
    quality: formData.value.quality,
    sale: formData.value.sale,
    afterSale: formData.value.afterSale,
    appeal: formData.value.appeal,
    insuranceCode: formData.value.insuranceCode,
    part_1: formData.value.part_1,
    part_2: formData.value.part_2,
    part_3: formData.value.part_3,
    part_4: formData.value.part_4,
    seriesEnergyTypeEnum: formData.value.seriesEnergyTypeEnum,
    vendorId: formData.value.vendorId,
    carVendorName: formData.value.carVendorInfo?.name,
  }))

  const filterParams = computed<OmitPageParam<ComplaintSearchParams>>(filterParamsHandler)
  function filterParamsHandler(): OmitPageParam<ComplaintSearchParams> {
    const params: OmitPageParam<ComplaintSearchParams> = {}
    if (formData.value.seriesEnergyTypeEnum)
      params.seriesEnergyTypeEnum = formData.value.seriesEnergyTypeEnum

    if (formData.value.vendorId)
      params.vendorId = formData.value.vendorId
    if (formData.value.title)
      params.title = formData.value.title.trim()
    if (formData.value.id) {
      const id = formData.value.id.trim()
      const idMatch = id.match(/(?:QT)?(\d+)/)
      if (idMatch && idMatch[1] && !Number.isNaN(Number(idMatch[1])))
        params.complainId = idMatch[1]
    }

    if (formData.value.timePicker) {
      params.startFirstFaultTime = timeFormat(formData.value.timePicker[0], 'YYYY-MM-DD')
      params.endFirstFaultTime = timeFormat(formData.value.timePicker[1], 'YYYY-MM-DD')
    }
    if (formData.value.carBrandId)
      params.brandId = formData.value.carBrandId
    if (formData.value.carSeriesId)
      params.searchId = formData.value.carSeriesId
    if (formData.value.seriesGrade)
      params.searchGradeEnum = formData.value.seriesGrade === 'UNLIMITED' ? undefined : formData.value.seriesGrade
    if (formData.value.areaIds[0])
      params.provinceId = formData.value.areaIds[0]
    if (params.provinceId && formData.value.areaIds[1])
      params.cityId = formData.value.areaIds[1]
    if (formData.value.insuranceCode)
      params.insuranceCode = formData.value.insuranceCode
    if (formData.value.quality.length || formData.value.sale.length || formData.value.afterSale.length || formData.value.appeal.length)
      params.issueId = [...formData.value.quality, ...formData.value.sale, ...formData.value.afterSale, ...formData.value.appeal].map(String)
    if (formData.value.part_1.length || formData.value.part_2.length || formData.value.part_3.length || formData.value.part_4.length)
      params.partsIds = [...formData.value.part_1, ...formData.value.part_2, ...formData.value.part_3, ...formData.value.part_4].map(String)

    return params
  }

  return {
    formRef,
    formData,
    carSelected,
    getShowValue,
    clearFilter,
    initRouteQuery,
    filterRouteQuery,
    filterParams,
    filterParamsHandler,

    // 修复时间选择器 | 地区清除后数据未变动
    pickerChangeShow,

    // 问题选项
    issuesState,
    issues,
    getIssuesHandle,
    // 零件选项
    partsState,
    parts,
    getPartsHandle,
  }
}

export function useSearchCollapseControl() {
  const show = ref(true)
  const autoShow = useLocalStorage<boolean>('pc-complaintSearchIsAuto', false)
  const filterFormRef = ref<HTMLElement>()
  const targetIsVisible = useElementVisibility(filterFormRef)

  useActivatedWatch(() => [
    watch(targetIsVisible, (nv) => {
      if (nv && !show.value && autoShow.value)
        show.value = true
    }),
  ])

  return {
    show,
    autoShow,
    filterFormRef,
  }
}
